import { useRef } from 'react';
import { Controller, type Control, type UseFormSetValue } from 'react-hook-form';
import {
  AppButton,
  AppCheckbox,
  AppForm,
  AppFormLabel,
  AppSelect,
  AppSpin,
  AppTextField,
} from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms';
import { dateValueToIso } from '@/utils/dateConverter';
import type { PatientFormSchemaType } from '@pages/main/patient/types';
import type { MedicalAidDirectSearchOptions } from '@pages/main/patient/hooks/usePatientEditPage.types';

interface MedicalAidSectionFormProps {
  control: Control<PatientFormSchemaType>;
  setValue: UseFormSetValue<PatientFormSchemaType>;
  isLoading: boolean;
  isExpired: boolean;
  insurerTypeId: number | null | undefined;
  isNoMedicalAid: boolean;
  isReimbursementMedicalAid: boolean;
  isPartialAidChecked: boolean;
  calcTypeString: string;
  medicalAidInsurerTypeOptions: Array<{ value: number; label: string }>;
  /** What the 種類 select shows, when it is not `insurerTypeId` itself (無し / 有り mode). */
  insurerTypeSelectValue?: number | null;
  /** Turns the picked option into the `insurer_type_id` to store; identity when absent. */
  toInsurerTypeId?: (picked: number) => number;
  onMedicalAidSearch: () => void;
  onMedicalAidNumberDirectSearch: (
    number: string,
    options?: MedicalAidDirectSearchOptions
  ) => Promise<void>;
  isMedicalAidDirectSearchLoading?: boolean;
  /**
   * Render the 「医療助成終了」 checkbox under the 適用 dates.
   *
   * `medical_aid.is_end_medical_aid` is already part of the form schema and of
   * `toFormValues`, but no screen has ever offered a control for it. Opt-in and
   * `false` by default, so every existing caller keeps the layout it has.
   */
  showMedicalAidEnd?: boolean;
  /**
   * Look the 公費負担者番号 up when the input loses focus, as 補助入力 does, and
   * clear it if the master has no such number. RA2N's own screen looks it up on
   * Enter only; absent keeps that.
   */
  searchNumberOnBlur?: boolean;
  /**
   * Lock 補助入力 while 無し is picked, with the rest of the section. RA2N's own
   * screen keeps it live; absent keeps that.
   */
  disableSearchWhenNoAid?: boolean;
  /**
   * The whole area is locked (the section's own `disabled`, or a `<fieldset disabled>` around it).
   *
   * A `<fieldset disabled>` only reaches NATIVE controls, and two controls here are not native:
   * the 種類 select, whose antd root is a `<div>`, and 適用開始日 / 適用終了日, whose 📅 opens
   * from a `<span role="button">` gated on this prop alone. Inside a locked fieldset both stayed
   * live: the select could still be switched 無し to 有り, and the calendars still opened.
   * `false` by default, so a caller that passes nothing keeps what it has.
   */
  disabled?: boolean;
}

export function MedicalAidSectionForm({
  control,
  setValue,
  isLoading,
  isExpired,
  insurerTypeId,
  isNoMedicalAid,
  isReimbursementMedicalAid,
  isPartialAidChecked,
  calcTypeString,
  medicalAidInsurerTypeOptions,
  insurerTypeSelectValue,
  toInsurerTypeId,
  onMedicalAidSearch,
  onMedicalAidNumberDirectSearch,
  isMedicalAidDirectSearchLoading = false,
  showMedicalAidEnd = false,
  searchNumberOnBlur = false,
  disableSearchWhenNoAid = false,
  disabled = false,
}: MedicalAidSectionFormProps) {
  // The number the field held on focus, so leaving it unchanged does not look it up again.
  const numberOnFocusRef = useRef('');
  return (
    <AppSpin spinning={isLoading}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-x-6">
          <div className="flex items-start">
            <AppSelect
              value={insurerTypeSelectValue === undefined ? insurerTypeId : insurerTypeSelectValue}
              onChange={value => {
                const newId = toInsurerTypeId ? toInsurerTypeId(value as number) : (value as number);
                const prevId = insurerTypeId;
                setValue('medical_aid.insurer_type_id', newId);
                // Only clear when the user changes type via this control — not when the modal
                // (or other code) sets insurer_type_id + number together.
                // UAT Sprint5 No.207 (HIT-14778) cancels No.40: only the 公費負担者番号 has to be
                // re-entered; 公費受給者番号 and 適用開始日/適用終了日 are kept. Switching to 無し
                // still resets everything (MedicalAidSection).
                if (prevId !== newId) {
                  setValue('medical_aid.medical_aid_number', '');
                  setValue('medical_aid.claim_type_id', null);
                }
              }}
              className="w-full"
              disabled={disabled}
              options={medicalAidInsurerTypeOptions}
            />
          </div>

          <div className="grid grid-cols-1 items-start gap-x-4 gap-y-3 sm:grid-cols-[150px_minmax(0,1fr)] sm:gap-x-6">
            <AppFormLabel label="公費負担者番号" />
            <div className="flex flex-wrap items-start gap-2">
              <div className="w-32 shrink-0">
                <Controller
                  name="medical_aid.medical_aid_number"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AppForm.Item
                      className="w-full mb-0"
                      validateStatus={fieldState.invalid ? 'error' : undefined}
                      help={fieldState.error?.message}
                    >
                      <AppTextField
                        {...field}
                        value={field.value ?? ''}
                        className={`w-32 ${isReimbursementMedicalAid ? 'border-pink-500' : ''}`}
                        disabled={disabled || isNoMedicalAid}
                        maxLength={255}
                        status={fieldState.invalid ? 'error' : undefined}
                        onFocus={e => {
                          numberOnFocusRef.current = e.currentTarget.value.trim();
                        }}
                        onBlur={e => {
                          field.onBlur();
                          if (!searchNumberOnBlur) return;
                          const val = e.currentTarget.value.trim();
                          if (!val || val === numberOnFocusRef.current) return;
                          void onMedicalAidNumberDirectSearch(val, { clearOnMiss: true });
                        }}
                        onChange={e => {
                          field.onChange(e);
                          // A re-typed 公費負担者番号 no longer matches the looked-up 請求区分.
                          // 適用開始日/適用終了日 are kept (UAT Sprint5 No.207).
                          setValue('medical_aid.claim_type_id', null);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const val = e.currentTarget.value.trim();
                            if (val) {
                              // The blur that follows Enter must not look the same
                              // number up a second time (two requests, two dialogs).
                              numberOnFocusRef.current = val;
                              void onMedicalAidNumberDirectSearch(val);
                            }
                          }
                        }}
                      />
                    </AppForm.Item>
                  )}
                />
              </div>
              <div className="shrink-0">
                <AppButton
                  type="primary"
                  onClick={onMedicalAidSearch}
                  loading={isMedicalAidDirectSearchLoading}
                  disabled={disabled || (disableSearchWhenNoAid && isNoMedicalAid)}
                  data-skip-enter-nav
                >
                  補助入力
                </AppButton>
              </div>
            </div>

            <AppFormLabel label="公費受給者番号" />
            <div className="flex flex-wrap items-start gap-2">
              <div className="w-32 shrink-0">
                <Controller
                  name="medical_aid.medical_aid_patient_number"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AppForm.Item
                      className="w-full mb-0"
                      validateStatus={fieldState.invalid ? 'error' : undefined}
                      help={fieldState.error?.message}
                    >
                      <AppTextField
                        {...field}
                        value={field.value ?? ''}
                        className="w-32"
                        disabled={disabled || isNoMedicalAid}
                        maxLength={255}
                        status={fieldState.invalid ? 'error' : undefined}
                      />
                    </AppForm.Item>
                  )}
                />
              </div>
              {isReimbursementMedicalAid && (
                <span className="shrink-0 rounded-sm border border-gray-300 bg-[#eeeeee] px-1 py-0.5 text-xl">
                  償還払い
                </span>
              )}
            </div>
          </div>

          <AppFormLabel label="医療助成負担" />
          <div className="flex flex-wrap items-center gap-2">
            <Controller
              name="medical_aid.insurer_cost_rate"
              control={control}
              render={({ field }) => (
                <AppTextField value={field.value ?? ''} disabled className="w-16 bg-gray-100" />
              )}
            />
            <span className="text-sm">割</span>
            <AppCheckbox checked={isPartialAidChecked} disabled>
              一部助成
            </AppCheckbox>
            {calcTypeString && <span className="text-sm">{calcTypeString}</span>}
            {isExpired && <span className="ml-4 text-xs font-bold text-red-500">有効期限外</span>}
          </div>
        </div>

        {/* HIT-4319: keep each date field in half of the row and wrap errors under its own input. */}
        <div className="grid w-full grid-cols-2 items-start gap-x-6 gap-y-2">
          <div className="flex min-w-0 items-start gap-x-2">
            <AppFormLabel label="適用開始日" />
            <Controller
              name="medical_aid.application_start_date"
              control={control}
              render={({ field, fieldState }) => (
                <AppForm.Item
                  className="mb-0 min-w-0 flex-1"
                  validateStatus={fieldState.invalid ? 'error' : undefined}
                  help={fieldState.error?.message}
                >
                  <RA2NDatePicker
                    value={field.value ?? ''}
                    onChange={val => field.onChange(dateValueToIso(val) ?? '')}
                    onBlur={field.onBlur}
                    className="w-full"
                    placeholder="元号00年00月00日"
                    disabled={disabled || isNoMedicalAid}
                    error={fieldState.invalid}
                  />
                </AppForm.Item>
              )}
            />
          </div>

          <div className="flex min-w-0 items-start gap-x-2">
            <AppFormLabel label="適用終了日" />
            <Controller
              name="medical_aid.application_end_date"
              control={control}
              render={({ field, fieldState }) => (
                <AppForm.Item
                  className="mb-0 min-w-0 flex-1"
                  validateStatus={fieldState.invalid ? 'error' : undefined}
                  help={fieldState.error?.message}
                >
                  <RA2NDatePicker
                    value={field.value ?? ''}
                    onChange={val => field.onChange(dateValueToIso(val) ?? '')}
                    onBlur={field.onBlur}
                    className="w-full"
                    placeholder="元号00年00月00日"
                    disabled={disabled || isNoMedicalAid}
                    error={fieldState.invalid}
                  />
                </AppForm.Item>
              )}
            />
          </div>

          {isExpired && (
            <div className="w-full mt-2 text-sm font-medium text-red-500">
              有効期限が切れています。
            </div>
          )}
        </div>

        {/* 医療助成終了 — its own row under the dates (opt-in, `showMedicalAidEnd`). */}
        {showMedicalAidEnd && (
          <div className="flex items-start">
            <Controller
              name="medical_aid.is_end_medical_aid"
              control={control}
              render={({ field }) => (
                <AppCheckbox
                  checked={!!field.value}
                  disabled={disabled || isNoMedicalAid}
                  onChange={event => field.onChange(event.target.checked)}
                >
                  医療助成終了
                </AppCheckbox>
              )}
            />
          </div>
        )}
      </div>
    </AppSpin>
  );
}
